import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Grid } from '@opendoor/atoms/grid';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { Toasty, useAtomsToastManager } from '@opendoor/atoms/toast';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import * as Sentry from '@sentry/nextjs';
import {
  dehydrate,
  type DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { requestZeusFromServer } from 'components/api/zeusServerClient';
import {
  marketplaceBrowseQueryOptions,
  type MarketplaceBrowseVariables,
} from 'components/marketplace/browse/marketplaceBrowse';
import {
  buildMarketInfoFromMarket,
  buildMarketInfoFromZone,
  fetchActiveBuyMarkets,
  fetchMarketRouteData,
  fetchZoneRouteData,
  parseMarketParam,
} from 'components/marketplace/helpers/ssr';
import { useMapRefs } from 'components/marketplace/helpers/useMapRefs';
import { useMarketplaceFavorite } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { useMarketplaceSearchController } from 'components/marketplace/helpers/useMarketplaceSearchController';
import {
  buildAddressPreviewParams,
  buildQueryParams,
  isUuid,
  parseQueryParams,
  parseSavedSearchId,
  removeAddressPreviewParams,
} from 'components/marketplace/helpers/useSearchQueries';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';
import {
  buildPayloadFromQuery,
  getInitialPropertyTypes,
  useSearchState,
} from 'components/marketplace/helpers/useSearchState';
import { useViewerMarketplaceFavorites } from 'components/marketplace/helpers/useViewerMarketplaceFavorites';
import type { MarketplacePropertySuggestionFragment } from 'components/marketplace/map/__generated__/location-search.zeus';
import { MarketplaceMap } from 'components/marketplace/map/MarketplaceMap';
import { MarketplaceMapMarkers } from 'components/marketplace/map/MarketplaceMapMarkers';
import { MiniPropertyDetailPage } from 'components/marketplace/map/MiniPropertyDetailPage';
import { MobileHeaderFilters } from 'components/marketplace/map/MobileHeaderFilters';
import { MobileMiniPropertyDetailPage } from 'components/marketplace/map/MobileMiniPropertyDetailPage';
import { PropertyList, PropertyListRefreshOverlay } from 'components/marketplace/map/PropertyList';
import { QuickFilters } from 'components/marketplace/map/QuickFilters';
import { MapSearchFilters } from 'components/marketplace/map/SearchFilters';
import {
  type MapLocation,
  type MarketInfo,
  MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS,
  MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
} from 'components/marketplace/map/types';
import { useMarketplaceListingPreview } from 'components/marketplace/map/useMarketplaceListingPreview';
import { useMobileMiniPDPSwipeDismiss } from 'components/marketplace/map/useMobileMiniPDPSwipeDismiss';
import { getShapeFromMap } from 'components/marketplace/map/utils';
import { MarketplaceSeoHead } from 'components/marketplace/MarketplaceSeoHead';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import { dollarToCentsString, toFilterString } from 'components/marketplace/search/filterHelpers';
import { DEFAULT_MARKET, getZoomForLocationType } from 'components/marketplace/search/utils';
import {
  MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
  SLIDE_PANEL_TRANSITION_CLASS,
} from 'components/marketplace/shared/panelTransitions';
import type { ActiveMarket } from 'components/marketplace/types';
import MarketplaceMapLayout from 'components/properties/shared/MarketplaceMapLayout';
import { useSmallerThanLG, useSmallerThanMD } from 'components/shared/hooks/useMediaQuery';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import { useObservability } from 'helpers/observability';
import type { MapInstance, ViewStateChangeEvent } from 'react-map-gl/mapbox';
import { useDebouncedCallback } from 'use-debounce';

const SearchErrorToast = ({
  hasError,
  clearError,
}: {
  hasError: boolean;
  clearError: () => void;
}) => {
  const toastManager = useAtomsToastManager();
  const toastManagerRef = useRef(toastManager);
  toastManagerRef.current = toastManager;
  const clearErrorRef = useRef(clearError);
  clearErrorRef.current = clearError;

  useEffect(() => {
    if (!hasError) {
      return;
    }
    toastManagerRef.current.add({
      title: 'We couldn’t update results. Please try again.',
      variant: 'error',
    });
    clearErrorRef.current();
  }, [hasError]);
  return null;
};

// mapbox merges a camera method's `eventData` into every event that method
// fires, so tagging our own `flyTo` lets `handleOnMoveEnd` tell the fly's
// trailing `moveend` apart from a user pan/zoom and skip it — otherwise the fly
// we run to center a freshly selected zone would immediately trigger a viewport
// search that clobbers that zone. This is robust to however many map instances
// echo the fly (and to a no-op fly firing no `moveend`); untagged moves (user
// gestures, the geolocate control) are unaffected and still refetch.
const ZONE_FLY_EVENT_DATA: { odSkipViewportSearch: true } = { odSkipViewportSearch: true };

const MarketplacePage: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  initialVariables,
  marketInfo,
  queryParams,
  activeMarkets,
  canonicalOverride,
  savedSearchId,
  initialZoneIds,
}) => {
  const { authentication, user } = useAuth();
  const { trackJourneyEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';
  const { mapRef } = useMapRefs();
  const isSmallerThanMD = useSmallerThanMD();
  const isSmallerThanLG = useSmallerThanLG();

  const searchState = useSearchState(queryParams);

  const {
    mapMarkers,
    listings,
    isLoading,
    isLoadingMore,
    hasError,
    clearError,
    selectedLocation,
    setSelectedLocation,
    selectedAddress,
    setSelectedAddress,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    loadMore,
    applySavedSearch,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
  } = useMarketplaceSearchController({
    initialVariables,
    searchState,
    marketInfo,
    initialVertices: queryParams.vertices,
    initialZoneIds,
    // `ssid` survives the hard navigation a cross-market saved-search apply
    // takes, restoring the active-search state on the destination page.
    initialSavedSearchId: savedSearchId,
  });

  const handleResetFilters = () => {
    searchState.resetFilters();
    clearActiveSavedSearch();
  };

  const handleApplySavedSearch = (savedSearch: Parameters<typeof applySavedSearch>[0]) => {
    void applySavedSearch(savedSearch);
  };

  const {
    minBeds,
    minBaths,
    minPrice,
    maxPrice,
    singleFamily,
    condo,
    townhome,
    noHoas,
    singleStory,
    mustHavePool,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    minSquareFootage,
    maxSquareFootage,
  } = searchState.get;

  // UX states
  const [errors, setErrors] = useState<Record<string, string>>();
  const [showList, setShowList] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showPdp, setShowPdp] = useState<boolean>(false);
  const customerId = user?.customerUuid;
  const { likedPropertyIds, initialFavoritesPending, onFavoriteChange } =
    useViewerMarketplaceFavorites(customerId, isLoggedIn);

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    queryParams.preview_aid ?? null,
  );
  const propertyPreview = useMarketplaceListingPreview(selectedAddressId);
  const {
    data: propertyDetails,
    isLoadingError: propertyPreviewLoadingFailed,
    isFetching: propertyPreviewIsFetching,
    isSuccess: propertyPreviewSucceeded,
    refetch: refetchPropertyPreview,
  } = propertyPreview;
  const loadingAddressId = propertyPreviewIsFetching ? selectedAddressId : null;
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: propertyDetails?.addressToken,
    initialFavoritePending: initialFavoritesPending,
    initialIsLiked: propertyDetails?.addressToken
      ? likedPropertyIds?.has(propertyDetails.addressToken)
      : undefined,
    origin: 'GALLERY',
    onFavoriteChange,
  });
  const propertyDetailsWithFavorite = propertyDetails
    ? { ...propertyDetails, isFavoriteLoading, isLiked, onToggleFavorite }
    : null;
  const previewAddressId = propertyDetails?.addressToken;
  const handleFetchPropertyDetails = useCallback(
    (addressId: string) => {
      if (addressId === selectedAddressId) {
        if (propertyPreviewLoadingFailed || (!propertyDetails && !propertyPreviewIsFetching)) {
          void refetchPropertyPreview();
        } else if (propertyDetails) {
          setShowPdp(true);
        }
        return;
      }
      setSelectedAddressId(addressId);
    },
    [
      propertyDetails,
      propertyPreviewLoadingFailed,
      propertyPreviewIsFetching,
      refetchPropertyPreview,
      selectedAddressId,
    ],
  );

  useEffect(() => {
    if (!selectedAddressId || previewAddressId !== selectedAddressId) {
      return;
    }
    setShowFilters(false);
    setShowPdp(true);
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: previewAddressId,
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview_aid') !== previewAddressId) {
      const urlParams = buildAddressPreviewParams(previewAddressId);
      window.history.pushState({}, '', `${window.location.pathname}?${urlParams}`);
    }
  }, [previewAddressId, selectedAddressId, trackJourneyEvent]);

  useEffect(() => {
    const previewUnavailable =
      propertyPreviewLoadingFailed || (propertyPreviewSucceeded && !propertyDetails);
    if (selectedAddressId && !propertyPreviewIsFetching && previewUnavailable) {
      setShowPdp(false);
    }
  }, [
    propertyDetails,
    propertyPreviewLoadingFailed,
    propertyPreviewIsFetching,
    propertyPreviewSucceeded,
    selectedAddressId,
  ]);

  const flyTo = (location: MapLocation) => {
    if (!location.coordinates) {
      return;
    }
    const zoom = getZoomForLocationType(location.zoneType);

    mapRef.current?.flyTo({ center: location.coordinates, zoom }, ZONE_FLY_EVENT_DATA);
  };

  const handleError = (key: string, val: string) => {
    if (val === '') {
      setErrors((previousErrors) => {
        if (!previousErrors?.[key]) {
          return previousErrors;
        }
        const nextErrors = { ...previousErrors };
        delete nextErrors[key];
        return Object.keys(nextErrors).length > 0 ? nextErrors : undefined;
      });
      return;
    }

    setErrors((previousErrors) => ({ ...previousErrors, [key]: val }));
  };

  useEffect(() => {
    if (marketInfo.identifier && !canonicalOverride) {
      const canonicalPath = buildCanonicalMarketPath(marketInfo.identifier, queryParams);
      if (`${window.location.pathname}${window.location.search}` !== canonicalPath) {
        window.history.replaceState({}, '', canonicalPath);
      }
    }
  }, [canonicalOverride, marketInfo.identifier, queryParams]);

  useEffect(() => {
    const handleHistoryChange = () => {
      const addressId = new URLSearchParams(window.location.search).get('preview_aid');
      setShowPdp(false);
      setSelectedAddressId(addressId && isUuid(addressId) ? addressId : null);
    };

    window.addEventListener('popstate', handleHistoryChange);
    return () => window.removeEventListener('popstate', handleHistoryChange);
  }, []);

  // Smaller filter menu on desktop that allows for immediate querying
  const handleQuickFilterChange = (bedrooms: string, bathrooms: string) => {
    handleSetBedrooms(bedrooms);
    handleSetBathrooms(bathrooms);
    void applyFilters({ bedrooms: Number(bedrooms), bathrooms: Number(bathrooms) });
  };

  // Mobile filter menu CTA click handler to trigger a search, close the filter
  // menu, and fly to the selected location.
  const handleMobileFilterClick = () => {
    if (selectedLocation) {
      flyTo(selectedLocation);
    }
    void (async () => {
      await applyFilters();
      setShowFilters(false);
    })();
  };

  // Desktop full-filter "Search" CTA. Wraps `applyFilters` so the press event
  // isn't forwarded as a filter override.
  const handleApplyFilters = useCallback(() => {
    void applyFilters();
  }, [applyFilters]);

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const setSearchAreaFromMapBounds = useCallback(
    (map: MapInstance) => applyViewport(getShapeFromMap(map)),
    [applyViewport],
  );

  const closePropertyDetails = useCallback(() => {
    setShowPdp(false);
    setSelectedAddressId(null);
    const urlParams = removeAddressPreviewParams();
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
  }, []);

  const mobileMiniPdpSwipe = useMobileMiniPDPSwipeDismiss(
    closePropertyDetails,
    showPdp && Boolean(propertyDetails),
  );

  const debouncedSetSearchAreaFromViewport = useDebouncedCallback(setSearchAreaFromMapBounds, 200);

  const handleOnMoveEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      // Skip the `moveend` from our own zone-centering fly (tagged in `flyTo`);
      // a user pan/zoom is untagged and still refetches the viewport.
      if ('odSkipViewportSearch' in e && e.odSkipViewportSearch === true) {
        return;
      }
      void debouncedSetSearchAreaFromViewport(e.target);
    },
    [debouncedSetSearchAreaFromViewport],
  );

  const handleSetBedrooms = (value: string) => searchState.set.setBedrooms(Number(value));
  const handleSetBathrooms = (value: string) => searchState.set.setBathrooms(Number(value));
  const handleMinPriceChange = (value: string) => searchState.set.setMinPrice(Number(value) / 100);
  const handleMaxPriceChange = (value: string) => searchState.set.setMaxPrice(Number(value) / 100);
  const handleSingleFamilyChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleFamily(!!checked);
  const handleCondoChange = (_: string, checked: string | boolean) =>
    searchState.set.setCondo(!!checked);
  const handleTownhomeChange = (_: string, checked: string | boolean) =>
    searchState.set.setTownhome(!!checked);
  const handleNoHoasChange = (_: string, checked: string | boolean) =>
    searchState.set.setNoHoas(!!checked);
  const handleSingleStoryChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleStory(!!checked);
  const handlePoolChange = (_: string, checked: string | boolean) =>
    searchState.set.setMustHavePool(!!checked);
  const handleMinYearBuiltChange = (value: string) =>
    searchState.set.setMinYearBuilt(Number(value));
  const handleMaxYearBuiltChange = (value: string) =>
    searchState.set.setMaxYearBuilt(Number(value));
  const handleMinLotSizeChange = (value: string) => searchState.set.setMinLotSize(Number(value));
  const handleMaxLotSizeChange = (value: string) => searchState.set.setMaxLotSize(Number(value));
  const handleMinSquareFootageChange = (min: string) =>
    searchState.set.setMinSquareFootage(Number(min));
  const handleMaxSquareFootageChange = (max: string) =>
    searchState.set.setMaxSquareFootage(Number(max));

  const handleShowList = () => {
    setShowList(true);
  };
  const handleShowMap = () => setShowList(false);
  const onFiltersButtonClick = () => {
    closePropertyDetails();
    setShowList(false);
    setShowPdp(false);
    setShowFilters(true);
  };

  const handleSetSelectedLocation = (location?: MapLocation) => {
    if (!location) {
      setSelectedLocation(undefined);
      return;
    }
    setIsMobileHeaderFocused(false);
    debouncedSetSearchAreaFromViewport.cancel();
    flyTo(location);
    void applyZoneSearch(location);
  };

  const handleSetSelectedAddress = (address?: MarketplacePropertySuggestionFragment) => {
    if (!address) {
      setSelectedAddress(undefined);
      return;
    }
    setSelectedAddress(address);
    handleFetchPropertyDetails(address.addressId);
    setIsMobileHeaderFocused(false);
  };

  const handleOnMobileFilterClick = () => {
    closePropertyDetails();
    setShowList(false);
    setShowFilters(true);
  };

  const [isMobileHeaderFocused, setIsMobileHeaderFocused] = useState(false);

  const handleOnMobileHeaderFocus = () => {
    setIsMobileHeaderFocused(true);
  };

  const handleOnMobileHeaderBlur = () => {
    setIsMobileHeaderFocused(false);
  };

  const RenderBody = (
    <MarketplaceMapLayout>
      <Toasty>
        <Col className="relative h-full w-full overflow-hidden border-t border-od-border-primary bg-od-bg-primary md:flex-row">
          {/** Header */}
          <Row
            onBlur={handleOnMobileHeaderBlur}
            onFocus={handleOnMobileHeaderFocus}
            className={cn(
              'fixed top-1.25 z-[8000] h-15 items-center justify-center transition-[width,left] duration-[250ms] md:hidden',
              isMobileHeaderFocused
                ? 'left-2.5 w-[95%]'
                : 'left-13.75 w-[calc(100%_-_var(--spacing)*50)]',
            )}
          >
            <Col className="w-full">
              <MobileHeaderFilters
                isFocused={isMobileHeaderFocused}
                setSelectedAddress={handleSetSelectedAddress}
                selectedAddress={selectedAddress}
                setSelectedLocation={handleSetSelectedLocation}
                selectedLocation={selectedLocation}
                onBack={handleOnMobileHeaderBlur}
                onOpenFilters={handleOnMobileFilterClick}
              />
            </Col>
          </Row>
          {/** Responsive map — one Mapbox instance for all breakpoints */}
          <Col
            aria-hidden={
              (isSmallerThanMD && (showList || showFilters)) ||
              (!isSmallerThanMD && isSmallerThanLG && showFilters)
            }
            className={cn(
              'relative h-screen w-full touch-none items-center border-t border-od-border-primary transition-[opacity,visibility] duration-500 md:h-full md:basis-1/2 lg:flex-1 lg:basis-1/2',
              (showList || showFilters) && 'max-md:invisible max-md:opacity-0',
              showFilters && 'md:invisible md:opacity-0 lg:visible lg:opacity-100',
            )}
          >
            <MarketplaceMap
              mapRef={mapRef}
              onClick={closePropertyDetails}
              onMoveEnd={handleOnMoveEnd}
              marketShape={marketInfo.shape}
            >
              <MarketplaceMapMarkers
                markers={mapMarkers}
                likedPropertyIds={likedPropertyIds}
                selectedAddressId={propertyDetails?.addressToken}
                loadingAddressId={loadingAddressId}
                onListingClick={handleFetchPropertyDetails}
              />
            </MarketplaceMap>
            <Button
              analyticsName="list-view"
              aria-label="List"
              onClick={handleShowList}
              size="lg"
              variant="secondaryInverse"
              className={cn(
                'fixed bottom-7.5 left-1/2 -translate-x-1/2 md:hidden',
                showList && 'hidden',
              )}
            >
              List
            </Button>
          </Col>
          {/** Fullscreen List (Small) */}
          <Col
            id="small-property-list"
            aria-hidden={!showList}
            className={cn(
              'absolute z-3 h-full w-full items-center overflow-y-scroll bg-od-bg-primary px-5 pb-32.5 md:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showList && 'invisible translate-y-[100vh] opacity-0',
            )}
          >
            <Col className="h-full w-full px-6">
              <Col className="flex-wrap justify-between pb-13">
                <PropertyListRefreshOverlay isRefreshing={isLoading && listings != null}>
                  <Text as="p" className="text-body-sm py-6">
                    {listings?.totalCount ?? 0} homes
                  </Text>
                  <PropertyList
                    isLoading={isLoading}
                    isLoadingMore={isLoadingMore}
                    handleOnSeeMore={loadMore}
                    listings={listings}
                    likedPropertyIds={likedPropertyIds}
                    initialFavoritesPending={initialFavoritesPending}
                    onFavoriteChange={onFavoriteChange}
                    firstPhotoLoadMode="deferred"
                  />
                </PropertyListRefreshOverlay>
                {activeMarkets && activeMarkets.length > 0 && !isLoading && (
                  <Col className="pt-12 pb-27.5">
                    <Text as="p" className="text-body-md pb-6 font-medium">
                      Popular Markets
                    </Text>
                    <Grid className="grid-cols-[repeat(auto-fill,minmax(calc(var(--spacing)*62.5),1fr))] gap-4">
                      <Link
                        href={`/homes-for-sale-near-me`}
                        analyticsName="market-link-near-me"
                        className="block"
                      >
                        <Text as="p" className="text-body-md text-od-content-secondary">
                          Homes for sale near me
                        </Text>
                      </Link>
                      {activeMarkets.map((market) => (
                        <Link
                          key={market.identifier}
                          href={`/homes/${market.identifier}`}
                          analyticsName={`market-link-${market.identifier}`}
                          className="block"
                        >
                          <Text as="p" className="text-body-md text-od-content-secondary">
                            Houses for sale in {market.displayName}
                          </Text>
                        </Link>
                      ))}
                    </Grid>
                  </Col>
                )}
              </Col>
            </Col>
            <Button
              analyticsName="list-view"
              aria-label="Map"
              onClick={handleShowMap}
              size="lg"
              variant="secondaryInverse"
              className={cn('fixed bottom-7.5 left-1/2 -translate-x-1/2', !showList && 'hidden')}
            >
              Map
            </Button>
          </Col>
          {/** Side List (Medium/Large) */}
          <Col
            id="property-list-container"
            aria-hidden={showPdp || showFilters}
            className={cn(
              'relative basis-1/2 border-t border-od-border-primary bg-od-bg-primary transition-[opacity,visibility] duration-500 max-md:hidden',
              (showPdp || showFilters) && 'invisible opacity-0',
            )}
          >
            <Col className="z-1 h-full w-full overflow-y-scroll px-12 pt-3">
              <QuickFilters
                bedrooms={String(minBeds)}
                bathrooms={String(minBaths)}
                selectedAddress={selectedAddress}
                setSelectedAddress={handleSetSelectedAddress}
                setSelectedLocation={handleSetSelectedLocation}
                selectedLocation={selectedLocation}
                onFilterChange={handleQuickFilterChange}
                onFiltersButtonClick={onFiltersButtonClick}
                isFocused={isMobileHeaderFocused}
                onApplySavedSearch={handleApplySavedSearch}
                activeSavedSearchId={activeSavedSearch?.id}
              />
              <PropertyListRefreshOverlay isRefreshing={isLoading && listings != null}>
                <Text as="p" className="text-body-sm pb-6">
                  {listings?.totalCount ?? 0} homes for sale near{' '}
                  {selectedLocation?.label || marketInfo.displayName || 'you'}
                </Text>
                <PropertyList
                  isLoading={isLoading}
                  isLoadingMore={isLoadingMore}
                  handleOnSeeMore={loadMore}
                  listings={listings}
                  likedPropertyIds={likedPropertyIds}
                  initialFavoritesPending={initialFavoritesPending}
                  onFavoriteChange={onFavoriteChange}
                  firstPhotoLoadMode="priority"
                />
              </PropertyListRefreshOverlay>
              {activeMarkets && activeMarkets.length > 0 && !isLoading && (
                <Col className="pt-12 pb-25">
                  <Text as="p" className="text-body-md pb-6 font-medium">
                    Popular Markets
                  </Text>
                  <Grid className="grid-cols-[repeat(auto-fill,minmax(calc(var(--spacing)*62.5),1fr))] gap-4">
                    <Link
                      href="/homes-for-sale-near-me"
                      analyticsName="market-link-near-me"
                      className="block"
                    >
                      <Text as="p" className="text-body-md text-od-content-secondary">
                        Homes for sale near me
                      </Text>
                    </Link>
                    {activeMarkets.map((market) => (
                      <Link
                        key={market.identifier}
                        href={`/homes/${market.identifier}`}
                        analyticsName={`market-link-${market.identifier}`}
                        className="block"
                      >
                        <Text as="p" className="text-body-md text-od-content-secondary">
                          Houses for sale in {market.displayName}
                        </Text>
                      </Link>
                    ))}
                  </Grid>
                </Col>
              )}
            </Col>
          </Col>
          {/** Fullscreen Filters (Small/Medium) */}
          <Col
            id="small-medium-filters"
            aria-hidden={!showFilters}
            className={cn(
              'fixed -top-1.25 z-[15000] h-full w-full items-center bg-od-bg-primary lg:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showFilters && 'invisible -translate-y-[100vh] opacity-0',
            )}
          >
            <MapSearchFilters
              minBeds={toFilterString(minBeds)}
              minBaths={toFilterString(minBaths)}
              minPrice={dollarToCentsString(minPrice)}
              maxPrice={dollarToCentsString(maxPrice)}
              singleFamily={singleFamily}
              condo={condo}
              townhome={townhome}
              noHoas={noHoas}
              singleStory={singleStory}
              mustHavePool={mustHavePool}
              minYearBuilt={toFilterString(minYearBuilt)}
              maxYearBuilt={toFilterString(maxYearBuilt)}
              minLotSize={toFilterString(minLotSize)}
              maxLotSize={toFilterString(maxLotSize)}
              minSquareFootage={toFilterString(minSquareFootage)}
              maxSquareFootage={toFilterString(maxSquareFootage)}
              handleError={handleError}
              handleMinBedsChange={handleSetBedrooms}
              handleMinBathsChange={handleSetBathrooms}
              handleMinSquareFootageChange={handleMinSquareFootageChange}
              handleMaxSquareFootageChange={handleMaxSquareFootageChange}
              handleMinPriceChange={handleMinPriceChange}
              handleMaxPriceChange={handleMaxPriceChange}
              handleSingleFamilyChange={handleSingleFamilyChange}
              handleCondoChange={handleCondoChange}
              handleTownhomeChange={handleTownhomeChange}
              handleNoHoasChange={handleNoHoasChange}
              handleSingleStoryChange={handleSingleStoryChange}
              handlePoolChange={handlePoolChange}
              handleMinYearBuiltChange={handleMinYearBuiltChange}
              handleMaxYearBuiltChange={handleMaxYearBuiltChange}
              handleMinLotSizeChange={handleMinLotSizeChange}
              handleMaxLotSizeChange={handleMaxLotSizeChange}
              onApplyFilters={handleMobileFilterClick}
              onResetFilters={handleResetFilters}
              onClose={handleCloseFilters}
              isLoading={isLoading}
              errors={errors}
              saveSearchQuery={getSaveSearchQuery()}
              onApplySavedSearch={handleApplySavedSearch}
              activeSavedSearch={activeSavedSearch}
              locationLabel={selectedLocation?.label}
              onSavedSearchCreated={markActiveSavedSearch}
            />
          </Col>
          {/** Side Filters (Large) */}
          <Col
            id="pdp-filters-container"
            aria-hidden={!showFilters}
            aria-label="Search filters"
            as="aside"
            className={cn(
              'absolute right-0 z-2 h-full w-1/2 border-t border-od-border-primary bg-od-bg-primary max-lg:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showFilters && 'invisible translate-x-[50vh] opacity-0',
            )}
          >
            <MapSearchFilters
              minBeds={toFilterString(minBeds)}
              minBaths={toFilterString(minBaths)}
              minPrice={dollarToCentsString(minPrice)}
              maxPrice={dollarToCentsString(maxPrice)}
              singleFamily={singleFamily}
              condo={condo}
              townhome={townhome}
              noHoas={noHoas}
              singleStory={singleStory}
              mustHavePool={mustHavePool}
              minYearBuilt={toFilterString(minYearBuilt)}
              maxYearBuilt={toFilterString(maxYearBuilt)}
              minLotSize={toFilterString(minLotSize)}
              maxLotSize={toFilterString(maxLotSize)}
              minSquareFootage={toFilterString(minSquareFootage)}
              maxSquareFootage={toFilterString(maxSquareFootage)}
              handleError={handleError}
              handleMinBedsChange={handleSetBedrooms}
              handleMinBathsChange={handleSetBathrooms}
              handleMinSquareFootageChange={handleMinSquareFootageChange}
              handleMaxSquareFootageChange={handleMaxSquareFootageChange}
              handleMinPriceChange={handleMinPriceChange}
              handleMaxPriceChange={handleMaxPriceChange}
              handleSingleFamilyChange={handleSingleFamilyChange}
              handleCondoChange={handleCondoChange}
              handleTownhomeChange={handleTownhomeChange}
              handleNoHoasChange={handleNoHoasChange}
              handleSingleStoryChange={handleSingleStoryChange}
              handlePoolChange={handlePoolChange}
              handleMinYearBuiltChange={handleMinYearBuiltChange}
              handleMaxYearBuiltChange={handleMaxYearBuiltChange}
              handleMinLotSizeChange={handleMinLotSizeChange}
              handleMaxLotSizeChange={handleMaxLotSizeChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              onClose={handleCloseFilters}
              isLoading={isLoading}
              errors={errors}
              saveSearchQuery={getSaveSearchQuery()}
              onApplySavedSearch={handleApplySavedSearch}
              activeSavedSearch={activeSavedSearch}
              locationLabel={selectedLocation?.label}
              onSavedSearchCreated={markActiveSavedSearch}
            />
          </Col>
          {/** Mini PDP (Medium/Large) */}
          <Col
            aria-hidden={!showPdp}
            className={cn(
              'absolute right-0 z-2 h-full w-1/2 overflow-y-scroll bg-od-bg-primary max-md:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showPdp && 'invisible translate-x-[50vh] opacity-0',
            )}
          >
            {propertyDetailsWithFavorite && (
              <MiniPropertyDetailPage
                property={propertyDetailsWithFavorite}
                onClose={closePropertyDetails}
                key={propertyDetailsWithFavorite.addressToken}
              />
            )}
          </Col>
          {/** Mobile Mini PDP (Small) */}
          <Col
            ref={mobileMiniPdpSwipe.panelRef}
            aria-hidden={!showPdp}
            className={cn(
              'absolute bottom-0 left-2.5 right-2.5 z-[3000] overflow-hidden rounded-t-4xl bg-od-bg-primary md:hidden',
              MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
              MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
              !showPdp && cn('invisible opacity-0', MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS),
              mobileMiniPdpSwipe.panelClassName,
            )}
          >
            {propertyDetails && (
              <MobileMiniPropertyDetailPage
                property={propertyDetails}
                onClose={closePropertyDetails}
              />
            )}
          </Col>
        </Col>
        <SearchErrorToast hasError={hasError} clearError={clearError} />
      </Toasty>
    </MarketplaceMapLayout>
  );

  return (
    <>
      <MarketplaceSeoHead
        marketInfo={marketInfo}
        activeMarkets={activeMarkets}
        canonicalOverride={canonicalOverride ?? undefined}
      />
      {RenderBody}
    </>
  );
};

const NOT_FOUND = { notFound: true } as const;

// Map of query params to their canonical path overrides
const CANONICAL_PATH_OVERRIDES: Record<string, string> = {
  is_near_me_page: '/homes-for-sale-near-me',
};

export const buildCanonicalMarketPath = (
  marketIdentifier: string,
  query: PropertySearchQuery,
): string => {
  const params = buildQueryParams({ ...query, market: null });
  const queryString = params.toString();
  return `/homes/${marketIdentifier}${queryString ? `?${queryString}` : ''}`;
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  dehydratedState: DehydratedState;
  initialVariables: MarketplaceBrowseVariables;
  marketInfo: MarketInfo;
  queryParams: PropertySearchQuery;
  activeMarkets: ActiveMarket[];
  canonicalOverride: string | null;
  savedSearchId: string | null;
  initialZoneIds: string[] | null;
}> = async (context) => {
  const queryParams = parseQueryParams(context.query);
  const savedSearchId = parseSavedSearchId(context.query);
  const route = parseMarketParam(queryParams.market);
  // Match SSR search precedence (vertices > zoneIds): a zid route that also
  // carries a viewport polygon must not seed routeZoneIds, or the controller
  // would force vertices to null on the next client search/save.
  const hasViewportPolygon = (queryParams.vertices?.length ?? 0) > 0;
  let initialZoneIds: string[] | null = null;
  if (!hasViewportPolygon) {
    initialZoneIds = route.kind === 'zone' ? [route.zoneId] : (queryParams.zoneIds ?? null);
  }

  // Check for canonical path overrides based on query params from cloudflare routing
  const overrideKey = Object.keys(CANONICAL_PATH_OVERRIDES).find(
    (key) => context.query[key] === 'true',
  );
  const canonicalOverride = overrideKey ? CANONICAL_PATH_OVERRIDES[overrideKey] : null;

  let activeMarketsPromise: Promise<ActiveMarket[]> = Promise.resolve([]);
  let marketInfo: MarketInfo | false = false;
  let resolvedMarket: string | null = null;

  if (route.kind === 'market') {
    const { market } = route;
    resolvedMarket = market.toLowerCase();
    activeMarketsPromise = fetchActiveBuyMarkets(context.req.headers);
    const routeData = await fetchMarketRouteData(market, context.req.headers);
    const requestedMarket = routeData.marketplace.market;
    const skipMarketFallback = context.query.skip_market_fallback === 'true';
    if (
      (!requestedMarket || !requestedMarket.active) &&
      !skipMarketFallback &&
      market.toLowerCase() !== DEFAULT_MARKET
    ) {
      return {
        redirect: {
          destination: `/homes/${DEFAULT_MARKET}`,
          permanent: false,
        },
      };
    }
    if (!requestedMarket) {
      return NOT_FOUND;
    }
    resolvedMarket = requestedMarket.identifier;
    marketInfo = buildMarketInfoFromMarket(requestedMarket, queryParams);
  } else if (route.kind === 'zone') {
    activeMarketsPromise = fetchActiveBuyMarkets(context.req.headers);
    const routeData = await fetchZoneRouteData(route.zoneId, context.req.headers);
    if (!routeData.marketplace.zone) {
      return NOT_FOUND;
    }
    marketInfo = buildMarketInfoFromZone(routeData.marketplace.zone, queryParams);
  } else {
    return NOT_FOUND;
  }

  if (!marketInfo) {
    return NOT_FOUND;
  }

  const initialVariables = buildPayloadFromQuery(
    { ...queryParams, ...getInitialPropertyTypes(queryParams) },
    {
      market: route.kind === 'market' ? resolvedMarket : null,
      vertices: hasViewportPolygon ? queryParams.vertices : null,
      zoneIds: hasViewportPolygon ? null : initialZoneIds,
    },
  );
  const queryClient = new QueryClient();
  let activeMarkets: ActiveMarket[];
  try {
    [, activeMarkets] = await Promise.all([
      queryClient.fetchInfiniteQuery(
        marketplaceBrowseQueryOptions({
          variables: initialVariables,
          request: (document, variables, signal) =>
            requestZeusFromServer(document, variables, {
              incomingRequestHeaders: context.req.headers,
              signal,
            }),
        }),
      ),
      activeMarketsPromise,
    ]);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'marketplace-browse', operation: 'initialBrowse' },
    });
    throw error;
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      initialVariables,
      marketInfo,
      queryParams,
      activeMarkets,
      canonicalOverride,
      savedSearchId,
      initialZoneIds,
    },
  };
};

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
        queryCache: createMarketplaceQueryCache(),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={props.dehydratedState}>
        <MarketplacePage
          key={JSON.stringify({
            initialVariables: props.initialVariables,
            queryParams: props.queryParams,
            savedSearchId: props.savedSearchId,
          })}
          {...props}
        />
      </HydrationBoundary>
    </QueryClientProvider>
  );
};

export default Page;
